import React from 'react';
import {View} from 'react-native';

import Icon from '/component/base/Icon';
import KeyboardInput from '/component/base/KeyboardInput';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';
import {showCopiedToClipboardToast} from '/util/toast.util';

import {SectionTitle} from '../../widgets/SectionTitle';
import {StaticFocusable} from '../StaticFocusable';

import {styles} from './WebsiteSection.style';
import type {WebsiteSectionProps} from './WebsiteSection.types';

const {websiteSection} = ManageMySiteTestIDs;

export const WebsiteSection = ({
  value = '',
  onChange = () => {},
  onBlur = () => {},
  baseWebsiteUrl = '',
  error = '',
  hasSubmitted,
}: WebsiteSectionProps) => {
  const cleanBaseWebsiteUrl = (baseWebsiteUrl || '').replace(/\/+$/, '');
  const cleanValue = (value || '').replace(/^\/+/, '');
  const fullUrl = [cleanBaseWebsiteUrl, cleanValue].filter(Boolean).join('/');

  const WEB_ALIAS_MAX_LENGTH = 100;
  const showError = hasSubmitted && !!error;

  const inputAccessibilityLabel = showError
    ? value
      ? `${value}, Website name, required, Error: ${error}`
      : `Website name, required, Error: ${error}`
    : value
    ? `${value}, Website name, required`
    : 'Website name, required';

  const handleCopy = () => {
    showCopiedToClipboardToast();
  };

  return (
    <View style={styles.section} testID={websiteSection.container}>
      <SectionTitle
        title={tt('website', 'Website')}
        accessibilityRole="header"
        required
      />

      <View style={[styles.inputWrapper, showError && styles.inputError]}>
        <KeyboardInput
          value={value}
          onBlur={onBlur}
          onChangeText={onChange}
          placeholder="Enter your website name"
          placeholderTextColor="#9CA3AF"
          autoCapitalize="none"
          accessible
          accessibilityRole="none"
          accessibilityLabel={inputAccessibilityLabel}
          accessibilityHint="Double tap to edit"
          accessibilityValue={{text: value || 'Empty'}}
          accessibilityState={showError ? {invalid: true} : undefined}
          testID={websiteSection.input}
          maxLength={WEB_ALIAS_MAX_LENGTH}
          style={[
            styles.input,
            // Platform.OS === 'web' && {outlineStyle: 'none'},
          ]}
          textAlignVertical="center"
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </View>

      {showError && (
        <StaticFocusable
          testID={websiteSection.error}
          label={`Website name error. ${error}`}
          role="alert">
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={styles.errorText}
            testID={`${websiteSection.error}-text`}
            accessible={false}>
            {error}
          </Text>
        </StaticFocusable>
      )}

      <View style={styles.shareLinkBox}>
        <StaticFocusable
          testID={websiteSection.shareLinkContainer}
          label="Your final share link"
          role="text">
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            testID={websiteSection.shareLinkLabel}
            variant="caption"
            style={styles.shareLinkLabel}
            accessible={false}
            isBold>
            {tt('yourFinalShareLink', 'YOUR FINAL SHARE LINK')}
          </Text>
        </StaticFocusable>

        <View style={styles.urlRow}>
          <TouchableOpacity
            style={styles.urlContainer}
            testID={websiteSection.shareLinkUrl}
            onPress={() => {}}
            disabled={!fullUrl}
            accessible
            accessibilityRole="link"
            accessibilityState={{disabled: !fullUrl}}
            accessibilityLabel={
              fullUrl ? `${fullUrl}, Coach website` : 'Coach website'
            }
            accessibilityHint="Opens your coach website in a browser">
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={websiteSection.shareLinkUrlText}
              style={styles.urlText}
              numberOfLines={1}
              ellipsizeMode="middle"
              accessible={false}
              isBold>
              {fullUrl}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            testID={websiteSection.copyButton}
            accessible
            onPress={handleCopy}
            style={styles.copyBtn}
            accessibilityRole="button"
            accessibilityLabel="Copy final share link"
            accessibilityHint="Copies the link to your clipboard">
            <Icon
              name="copy"
              accessible={false}
              style={styles.copyIcon}
              testID={websiteSection.copyIcon}
              importantForAccessibility="no-hide-descendants"
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};
